import { isBootFeatureEnabled } from 'owa-metatags';
import { action, mutatorAction } from 'satcheljs';
import { getLocalizedStringStore } from '../store/store';
import { fetchLocstringFile } from './fetchLocstringFile';
import { getScriptBackupPath, getScriptPath, isGulping } from 'owa-config';
import { setRTL } from '@fluentui/utilities/lib/rtl';
import { setItem } from 'owa-local-storage';
import { LocalStoreLocaleKey, clearSuccessfulLoadedUrls } from 'owa-localize-internal';
import type { WindowData, LocWindow } from 'owa-window-data';
import { getWindowData } from 'owa-window-data';
import { logUsage } from 'owa-analytics';
import { setCssDirection } from 'owa-inject-css';
import setPublicPath from 'owa-bundling-light/lib/utils/setPublicPath';
import type { MailboxInfo } from 'owa-client-types';

// TODO: Update types
type WindowDataExtended = WindowData & LocWindow;

export interface UserInitiatedParameters {
    userInitiated: true;
    mailboxInfo: MailboxInfo;
}

export async function setLocale(
    locale: string,
    dir: 'ltr' | 'rtl',
    culture?: string,
    userInitiated?: UserInitiatedParameters
): Promise<void> {
    const wd: WindowDataExtended = getWindowData() as WindowDataExtended;

    // Normalize locale
    locale = locale.toLowerCase();
    const { currentLocale, currentCulture } = getLocalizedStringStore();
    if (currentLocale !== locale || (culture && currentCulture !== culture)) {
        setItem(self, LocalStoreLocaleKey, locale);
        changeLocaleInStore(locale, culture);

        let dirChanged = false;
        const doc = wd.document;
        if (doc && dir && doc.dir != dir) {
            dirChanged = true;
            doc.dir = dir;
        }
        if (userInitiated?.userInitiated) {
            clearSuccessfulLoadedUrls();
            logUsage('LocaleChanged', userInitiated.mailboxInfo, {
                locale,
                dirChanged_1: dirChanged,
                dir_2: dir,
            });
        }

        const isDirRtl = dir === 'rtl';
        setCssDirection(isDirRtl);

        // setRTL from utilities will set the cached variable, local storage as well as merging the styles.
        setRTL(isDirRtl, true);

        onLocaleChanged(locale);
        if (!isGulping()) {
            const fetchBootLocStrings = () =>
                wd._locStrings?.registerHandler(locale, url => fetchLocstringFile(locale, url));
            try {
                await fetchBootLocStrings();
            } catch {
                // If we fail to register the handler, we need to set the public path back to the backup
                // and retry the fetch
                setPublicPath(getScriptBackupPath());
                // we don't await here to avoid unnecessary side-effects. URL generation will be executed synchronously
                const fetchPromise = fetchBootLocStrings();
                setPublicPath(getScriptPath());
                await fetchPromise;
            }

            if (isBootFeatureEnabled('fwk-loc-single-v2')) {
                forceLocObserversToRerender();
            }
        }
    }
}

export interface ON_LOCALE_CHANGEDActionMessage {
    locale: string;
}
export const onLocaleChanged: (locale: string) => ON_LOCALE_CHANGEDActionMessage = action(
    'ON_LOCALE_CHANGED',
    (locale: string): ON_LOCALE_CHANGEDActionMessage => ({ locale })
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const changeLocaleInStore = mutatorAction(
    'MUTATE_LOCSTRING_LOCALE',
    (locale: string, culture?: string) => {
        getLocalizedStringStore().currentLocale = locale;

        if (culture) {
            getLocalizedStringStore().currentCulture = culture;
        }
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const forceLocObserversToRerender: () => void = mutatorAction(
    'MUTATE_LOCSTRING_VERSION',
    (): void => {
        getLocalizedStringStore().version++;
    }
);
